import { country, countryName } from "./customers.js";

/**
 * Where a cold letter may go, by the law of the country of the company it goes to
 * ([23](../../../docs/23-customers.md) §the law, [ADR 0065](../../../docs/adr/0065-a-cold-letter-goes-only-where-the-law-of-the-company-s-country-has-been-read-and-allows-it.md)).
 *
 * **A rule in code, not a setting.** Nobody can switch it, because it is not an opinion about who is a good customer:
 * a country whose law wants a company's consent before a commercial email reaches it gets no letter from this
 * product, whoever asks. It is asked on the last line before the mailbox (`maySend`, for a first letter and every
 * follow-up), and earlier wherever effort or credit would otherwise be spent on a letter that can never go: before
 * an opening line is paid for, before a draft is approved, by the loop when it plans its week, and on the screens
 * that add a place, open a company and list the morning's letters.
 *
 * **A country nobody has read is held.** The table is what was read, on 2026-09-16, when Overbill's Reach wrote
 * it (Overbill `docs/50` §10); a country missing from it is not a country found to be fine, it is one nobody has
 * looked at, so its letters wait until somebody reads its law and adds a row here. Adding one is a commit, with the
 * rule it was read from in its `law`.
 *
 * **A held company is still kept.** The law refuses an unsolicited email, not knowing who a company is: it can be
 * found, read and judged, and reached by an introduction or a call. Only the letter is refused.
 */

export interface CountryLaw {
  /** Whether a cold letter may go to a company there at all. */
  goes: boolean;
  /** The rule as it was read, for whoever adds the next row or asks why. Never shown as the reason on a screen. */
  law: string;
  /** Why it is held, in words for the screen: the law and what it wants. Only for a country that holds. */
  holds?: string;
}

/** The day the table was read. A row added later says its own date in its `law`. */
export const COLD_LETTER_LAW_READ = "2026-09-16";

/**
 * Every country whose law on a cold commercial email to a company has been read.
 *
 * What *goes* asks of every letter — a sender who is who they say, an honest subject, the company's postal address,
 * a way to stop that works — is what every letter already carries (the frame and the footer, `letterFrame`), so a
 * country that sends needs nothing more from here than its row.
 */
export const COLD_LETTER_LAW: Readonly<Record<string, CountryLaw>> = {
  US: { goes: true, law: "CAN-SPAM: no consent needed; an honest header and subject, the sender's postal address, and an opt-out honoured within ten business days" },
  GB: { goes: true, law: "PECR: a registered company (Ltd, LLP) may be written to with a way to stop; a sole trader or a partnership counts as a person and needs consent" },
  FR: { goes: true, law: "CNIL: legitimate interest, for a professional address and about that person's work, with a way to stop in every message" },
  BR: { goes: true, law: "LGPD: legitimate interest, with a way to stop" },
  MX: { goes: true, law: "a privacy notice and a way to stop; the REPEP registry" },
  CL: { goes: true, law: "a privacy notice and a way to stop" },
  CO: { goes: true, law: "a privacy notice and a way to stop" },
  AR: { goes: true, law: "a privacy notice and a way to stop" },
  AE: { goes: true, law: "PDPL allows legitimate interest; the regulator expects the sender identified, a working way to stop, and low volume" },
  QA: { goes: true, law: "the sender identified, a working way to stop, and low volume" },
  KW: { goes: true, law: "the sender identified, a working way to stop, and low volume" },
  BH: { goes: true, law: "the sender identified, a working way to stop, and low volume" },
  OM: { goes: true, law: "the sender identified, a working way to stop, and low volume" },
  SA: {
    goes: false,
    law: "PDPL as implemented by SDAIA, and the anti-spam regulation: prior consent for a commercial electronic message",
    holds: "Saudi Arabia's law wants a company's consent before a commercial email reaches it (the PDPL and its anti-spam rules)",
  },
  ES: {
    goes: false,
    law: "LSSI-CE article 21: prior authorisation, or an existing relationship for similar products; the AEPD fines it",
    holds: "Spain's law wants permission before a commercial email is sent (LSSI-CE, article 21)",
  },
  TN: {
    goes: false,
    law: "Law 2004-63: a declaration to the INPDP to process personal data, and its authorisation to move it out of the country; held until there is local advice",
    holds: "Tunisia's law wants a declaration to its data authority, and its permission to keep the data outside the country, before anybody there is written to (INPDP)",
  },
};

/** Every country a letter may go to, for a query that must leave the rest out (the loop's next cell). */
export const LETTERS_GO_TO: readonly string[] = Object.entries(COLD_LETTER_LAW).filter(([, r]) => r.goes).map(([c]) => c);

/** The sentence every held company carries after its reason: what can still be done with it. */
export const HELD_STILL_KEPT = "The company can still be kept, for an introduction or a call.";

export type LettersGo =
  | { goes: true; law: string }
  | {
    goes: false;
    /** `law`: its law wants consent first. `unread`: nobody has read its law yet. */
    because: "law" | "unread";
    /** Why, in words a person reads on the screen, ending in what that means for the letter. */
    says: string;
  };

/**
 * Whether a cold letter may go to a company in this country, and if not, why in words.
 *
 * Pure, and the only place this is decided: the gate, the drafting, the loop and the screens all ask it, so there is
 * no second opinion anywhere about which countries a letter may reach. A code that is not a country is held, because
 * a company whose country is unknown is a company whose law is unknown.
 */
export function lettersGo(rawCountry: string): LettersGo {
  const c = country(rawCountry);
  const rule = c ? COLD_LETTER_LAW[c] : undefined;
  if (rule?.goes) return { goes: true, law: rule.law };
  if (rule?.holds) return { goes: false, because: "law", says: `${rule.holds}, so no letter goes there.` };
  return {
    goes: false, because: "unread",
    says: `Nobody has read ${c ? countryName(c) : "this country"}'s law on commercial email yet, so no letter goes there until somebody has.`,
  };
}
